import { Grid3x3, Smartphone, ShoppingBag, BarChart3, Activity, ClipboardCheck, ClipboardList, IdCard, Folder, TrendingUp, Inbox, Menu, LineChart, Scale } from "lucide-react";
import { PageFooter } from "@/components/PageFooter";
import { useLang } from "@/i18n";

const buildSections = (profile) => {
  const sections = [
    {
      title: "Explore",
      items: [
        { id: "home", label: "All Products", icon: Grid3x3, view: "home" },
        { id: "myfora", label: "myFORA", icon: Activity, view: "home#category-section-myfora" },
        { id: "digital-menu", label: "Digital Menu", icon: Smartphone, view: "home#category-section-digital-menu" },
        { id: "ordering", label: "Ordering & Payments", icon: ShoppingBag, view: "home#category-section-ordering-payments" },
        { id: "engagement", label: "Engagement & Data", icon: BarChart3, view: "home#category-section-engagement-data" },
      ],
    },
    {
      title: "Get Started",
      items: [
        { id: "onboarding", label: "Start Onboarding", icon: ClipboardCheck, view: "onboarding" },
        { id: "setup", label: "Country Setup", icon: ClipboardList, view: "setup" },
        { id: "contacts", label: "Contacts", icon: IdCard, view: "contacts" },
        { id: "repository", label: "Resources", icon: Folder, view: "repository" },
        { id: "data", label: "For BU Owners", icon: TrendingUp, view: "data" },
        { id: "submissions", label: "Submissions", icon: Inbox, view: "submissions" },
        { id: "legal", label: "Legal & Confidentiality", icon: Scale, view: "legal" },
      ],
    },
  ];

  // Country-scoped private dashboard (e.g. MK owners).
  if (profile?.dashboardUrl) {
    sections.push({
      title: `${profile.countryLabel} Only`,
      items: [
        {
          id: "country-dashboard",
          label: profile.dashboardLabel || `${profile.countryLabel} Dashboard`,
          icon: LineChart,
          view: "country-dashboard",
        },
      ],
    });
  }

  return sections;
};

// Map product IDs → their sidebar category for highlight
const productCategoryMap = {
  "myfora": "myFORA",
  "digital-menu": "Digital Menu",
  "pay-at-table": "Ordering & Payments",
  "pos-register": "Ordering & Payments",
  "pickup": "Ordering & Payments",
  "videri-signage": "Engagement & Data",
  "loyalty": "Engagement & Data",
  "rms": "Engagement & Data",
  "pepe-ai": "Engagement & Data",
  "fora-intelligence": "Engagement & Data",
};

// Map view → sidebar label (for active highlight)
const viewLabelMap = {
  home: "All Products",
  product: "Digital Menu",
  countries: "Digital Menu",
  data: "For BU Owners",
  repository: "Resources",
  onboarding: "Start Onboarding",
  setup: "Country Setup",
  submissions: "Submissions",
  contacts: "Contacts",
  legal: "Legal & Confidentiality",
  "country-dashboard": "__country_dashboard__",
};

// Map home anchor keys → sidebar label
const anchorLabelMap = {
  "home#category-section-myfora": "myFORA",
  "home#category-section-digital-menu": "Digital Menu",
  "home#category-section-ordering-payments": "Ordering & Payments",
  "home#category-section-engagement-data": "Engagement & Data",
};

const getActiveLabel = (activeView, profile) => {
  if (anchorLabelMap[activeView]) return anchorLabelMap[activeView];
  if (activeView.startsWith("product:")) {
    const pid = activeView.split(":")[1];
    return productCategoryMap[pid] || "All Products";
  }
  const mapped = viewLabelMap[activeView];
  if (mapped === "__country_dashboard__") {
    return profile?.dashboardLabel || `${profile?.countryLabel || ""} Dashboard`;
  }
  return mapped || "All Products";
};

export const Sidebar = ({ activeView, onNavigate, profile }) => {
  const { t } = useLang();
  const active = getActiveLabel(activeView, profile);
  const sections = buildSections(profile);

  return (
    <aside
      className="w-64 bg-white border-r border-[#e8e8e8] h-screen flex flex-col"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-14 sm:h-16 px-6 flex items-center gap-3 border-b border-[#e8e8e8] shrink-0">
        <img src="/coca-cola-hbc-logo.png" alt="Coca-Cola HBC" className="h-7" />
        <span className="w-px h-6 bg-[#e8e8e8]" />
        <img src="/fora-logo.png" alt="FORA" className="h-5" />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-6 px-3">
        {sections.map((section) => (
          <div key={section.title} className="mb-8">
            <p className="cchbc-eyebrow mb-4 px-4" data-testid={`sidebar-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
              {t(section.title)}
            </p>
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = active === item.label;
              // Build a shareable href (hash-style anchor) so right-click "open in new tab" works
              const href = item.view.startsWith("home#") ? `/#${item.view.split("#")[1]}` : `/#${item.view}`;
              return (
                <a
                  key={item.id}
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(item.view);
                  }}
                  className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] transition-colors mb-0.5 border-l-[3px] ${
                    isActive
                      ? "text-[#000000] font-bold bg-[#fafafa] border-[#dd260e] rounded-r-sm"
                      : "text-[#555555] hover:bg-[#fafafa] hover:text-[#000000] border-transparent"
                  }`}
                  data-testid={`sidebar-item-${item.id}`}
                >
                  <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                  <span>{t(item.label)}</span>
                </a>
              );
            })}
          </div>
        ))}
      </nav>

      <PageFooter compact />
    </aside>
  );
};

export { Menu };
